'use client';

import { useTranslations } from 'next-intl';
import { HelpCircle } from 'lucide-react';
import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { cn } from '@/shared/lib/cn';
import type { ProjectAttributes } from '@/types/project';

export type FacadeAreaUnit = NonNullable<ProjectAttributes['facade_area_unit']>;

/**
 * Square feet per square metre. Single source of truth for the in-field unit
 * toggle conversion — MUST match the server's rounding exactly (the server is
 * the canonical m² store; a different factor here would drift the displayed
 * value away from what the server persists).
 */
export const SQFT_PER_SQM = 10.7639104;

/**
 * Convert a facade-area value between display units, matching the server's
 * rounding precisely:
 *  - ft² → m²: round to 1 decimal place
 *  - m² → ft²: round to whole number
 * Returns the input unchanged when `from === to`.
 */
export function convertFacadeArea(
  value: number,
  from: FacadeAreaUnit,
  to: FacadeAreaUnit
): number {
  if (from === to) return value;
  if (from === 'ft2' && to === 'm2') {
    return Math.round((value / SQFT_PER_SQM) * 10) / 10;
  }
  // m2 → ft2
  return Math.round(value * SQFT_PER_SQM);
}

/**
 * Buffer-string display formatting: drop a trailing ".0" so whole numbers show
 * as e.g. "250" rather than "250.0" after an m² → ft² conversion. Shared so the
 * toggle and the seed path format identically.
 */
export function formatFacadeAreaValue(value: number): string {
  return String(value);
}

interface FacadeAreaFieldProps {
  id: string;
  /** Local string buffer (same plain-buffer pattern as goals/region). */
  value: string;
  /** Local display unit (mutable; toggling switches it immediately). */
  unit: FacadeAreaUnit;
  /** Show the "estimated from photos" caption under the field. */
  isEstimated: boolean;
  disabled?: boolean;
  /**
   * Override the field-label style. The standalone Brief page styles all its
   * input titles as section eyebrows (grey uppercase) to match the "Source
   * materials" section header; the render-settings sheet and project-settings
   * panel keep the default `text-form-label`. Omit to keep the default.
   */
  labelClassName?: string;
  onChange: (raw: string) => void;
  /** Switch the display unit; converts the current buffer and triggers a save. */
  onUnitChange: (unit: FacadeAreaUnit) => void;
  /** Optional: goals editors commit explicitly now, so blur-save is no longer wired. */
  onBlur?: () => void;
}

/** Keep only digits and a single decimal separator (comma normalized to dot). */
export function sanitizeFacadeAreaInput(raw: string): string {
  const normalized = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const [head = '', ...rest] = normalized.split('.');
  return rest.length > 0 ? `${head}.${rest.join('')}` : head;
}

/**
 * Buffer string → PATCH value. Empty clears the area (null); anything
 * non-numeric is "not a valid edit" (undefined → field omitted).
 */
export function parseFacadeArea(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Optional facade-area input for the project goals surfaces (standalone page,
 * render-settings sheet, project settings panel). The value is entered in the
 * project's display unit (server-resolved m²/ft²); estimates use it as the
 * authoritative area. When the server back-filled the value from an LLM
 * estimate, a caption says so until the user takes ownership by editing.
 */
export function FacadeAreaField({
  id,
  value,
  unit,
  isEstimated,
  disabled = false,
  onChange,
  onUnitChange,
  onBlur,
  labelClassName,
}: FacadeAreaFieldProps) {
  const t = useTranslations('project');

  // Compact segmented unit toggle, neutral grey active pill (the unit is a minor
  // setting — it shouldn't compete with the brand-teal primary CTA), rendered
  // inside the input on the trailing edge. Order ft² then m² matches siblings.
  const units: FacadeAreaUnit[] = ['ft2', 'm2'];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Label htmlFor={id} className={labelClassName}>
          {t('facadeArea')}
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <p className="text-form-help text-muted-foreground">
              {t('facadeAreaHelpText')}
            </p>
          </PopoverContent>
        </Popover>
      </div>
      <div className="relative">
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(sanitizeFacadeAreaInput(e.target.value))}
          onBlur={onBlur}
          placeholder={t('facadeAreaPlaceholder')}
          maxLength={10}
          disabled={disabled}
          className="pe-20"
          // Clarity opt-in: facade area is a numeric measurement, not PII —
          // unmask it in session replays (Clarity masks all inputs by default).
          data-clarity-unmask="true"
        />
        <div
          role="group"
          className="absolute inset-y-0 end-2 flex items-center gap-0.5"
        >
          {units.map((u) => {
            const active = u === unit;
            return (
              <button
                key={u}
                type="button"
                disabled={disabled}
                aria-pressed={active}
                onClick={() => {
                  if (active) return;
                  onUnitChange(u);
                }}
                className={cn(
                  'rounded-full px-2 py-0.5 text-form-help transition-colors disabled:opacity-50',
                  active
                    ? 'bg-foreground/10 text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {u === 'ft2'
                  ? t('facadeAreaUnits.ft2')
                  : t('facadeAreaUnits.m2')}
              </button>
            );
          })}
        </div>
      </div>
      {isEstimated && (
        <p className="text-form-help text-muted-foreground">
          {t('facadeAreaEstimatedCaption')}
        </p>
      )}
    </div>
  );
}
